/**
 * Leg search (blob leg-search block). A searchable dropdown
 * inside the line-plot leg-overlay row: type a leg key, pick it, and the chart
 * zooms to that leg's window (±3h) via the date atoms + a replot. The full leg
 * list is fetched lazily once per ship (`GET /legs/all`) and cached on `runtime`,
 * invalidated on ship change.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { runtime } from './plot-runtime.ts';
import { doPlot } from './plot-actions.ts';
import { selectedShip, startDate, endDate } from '../../state/store.ts';
import { getAllLegs } from '../../api/endpoints.ts';
import { fmtUtcDatetime } from '../../utils/dates.ts';
import { parseLegSec } from './render/format.ts';
import { setPresetActive } from '../ship/date-presets.ts';
import type { Leg } from '../../api/types.ts';

async function ensureAllLegs(): Promise<Leg[]> {
  const shipId = selectedShip.value;
  if (!shipId) return [];
  if (runtime.allLegsShipId !== shipId) {
    runtime.allLegs = null;
    runtime.allLegsPromise = null;
    runtime.allLegsShipId = shipId;
  }
  if (runtime.allLegs) return runtime.allLegs;
  if (runtime.allLegsPromise) return runtime.allLegsPromise;
  runtime.allLegsPromise = (async () => {
    try {
      const resp = await getAllLegs(shipId);
      const legs = resp && resp.supported && Array.isArray(resp.legs) ? resp.legs : [];
      if (runtime.allLegsShipId === shipId) runtime.allLegs = legs;
      return legs;
    } catch (e) {
      console.warn('All-legs fetch failed', e);
      return [];
    } finally {
      runtime.allLegsPromise = null;
    }
  })();
  return runtime.allLegsPromise;
}

@customElement('leg-search')
export class LegSearch extends LightElement {
  private store = new StoreController(this, [selectedShip]);

  @state() private query = '';
  @state() private open = false;
  @state() private highlight = 0;

  private lastShip: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // The menu is position:fixed (so the panel's overflow can't clip it), which
    // means it doesn't follow the input on scroll/resize - re-anchor it instead.
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }

  override updated(): void {
    // Invalidate the input when the ship changes (cache invalidation lives in
    // ensureAllLegs; here we just reset the visible query).
    if (selectedShip.value !== this.lastShip) {
      this.lastShip = selectedShip.value;
      this.query = '';
      this.open = false;
    }
    this.reposition();
  }

  /** Anchor the fixed-position menu to the input's current viewport rect,
   * clamping to the right edge so a wide leg key never spills off-screen. */
  private reposition = (): void => {
    if (!this.open) return;
    const input = this.querySelector<HTMLElement>('.leg-search-input');
    const menu = this.querySelector<HTMLElement>('.leg-search-menu');
    if (!input || !menu) return;
    const r = input.getBoundingClientRect();
    menu.style.minWidth = `${r.width}px`;
    menu.style.top = `${r.bottom + 4}px`;
    const left = Math.min(r.left, window.innerWidth - menu.offsetWidth - 8);
    menu.style.left = `${Math.max(8, left)}px`;
  };

  private matchedLegs(): Leg[] {
    const legs = runtime.allLegs || [];
    const q = this.query.trim().toLowerCase();
    if (!q) return legs.slice(0, 200);
    return legs.filter((l) => String(l.leg_key || '').toLowerCase().includes(q)).slice(0, 200);
  }

  private async onFocus(): Promise<void> {
    this.open = true;
    const p = ensureAllLegs();
    this.requestUpdate();
    await p;
    this.requestUpdate();
  }

  private onInput(e: Event): void {
    this.query = (e.target as HTMLInputElement).value;
    this.highlight = 0;
  }

  private onBlur(): void {
    // Delay so a mousedown on an item picks before the menu closes.
    setTimeout(() => {
      this.open = false;
      this.requestUpdate();
    }, 150);
  }

  private onKeydown(e: KeyboardEvent): void {
    const items = this.matchedLegs();
    if (e.key === 'Escape') {
      this.open = false;
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlight = Math.min(items.length - 1, this.highlight + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlight = Math.max(0, this.highlight - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = items[this.highlight] || items[0];
      if (pick) this.pickLeg(pick);
    }
  }

  private pickLeg(leg: Leg): void {
    if (!leg || !leg.start || !leg.end) return;
    const sMs = parseLegSec(leg.start) * 1000 - 3 * 3600 * 1000;
    const eMs = parseLegSec(leg.end) * 1000 + 3 * 3600 * 1000;
    if (!Number.isFinite(sMs) || !Number.isFinite(eMs)) return;
    startDate.set(fmtUtcDatetime(new Date(sMs)));
    endDate.set(fmtUtcDatetime(new Date(eMs)));
    setPresetActive('custom');
    this.query = String(leg.leg_key || '');
    this.open = false;
    void doPlot();
  }

  private renderMenu(): TemplateResult | typeof nothing {
    if (!this.open) return nothing;
    if (!runtime.allLegs && runtime.allLegsPromise) {
      return html`<div class="leg-search-menu"><div class="leg-search-loading">Loading legs…</div></div>`;
    }
    const legs = runtime.allLegs || [];
    if (!legs.length) {
      return html`<div class="leg-search-menu"><div class="leg-search-empty">No legs available</div></div>`;
    }
    const items = this.matchedLegs();
    if (!items.length) {
      return html`<div class="leg-search-menu"><div class="leg-search-empty">No matches</div></div>`;
    }
    return html`<div class="leg-search-menu">
      ${items.map(
        (l, i) => html`<div
          class="leg-search-item ${i === this.highlight ? 'active' : ''}"
          @mousedown=${(e: MouseEvent) => {
            e.preventDefault();
            this.pickLeg(l);
          }}
          @mouseenter=${() => {
            this.highlight = i;
          }}
        >
          <span class="leg-search-key">${String(l.leg_key || '')}</span>
        </div>`,
      )}
    </div>`;
  }

  override render(): TemplateResult {
    return html`
      <div class="leg-search">
        <input
          type="text"
          class="leg-search-input"
          placeholder="Search legs…"
          .value=${this.query}
          @focus=${() => void this.onFocus()}
          @input=${(e: Event) => this.onInput(e)}
          @blur=${() => this.onBlur()}
          @keydown=${(e: KeyboardEvent) => this.onKeydown(e)}
        />
        ${this.renderMenu()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'leg-search': LegSearch;
  }
}
